import { BaseTextFieldProps, TextField, TextFieldProps } from '@mui/material';
import React from 'react';
import { Control, Controller } from 'react-hook-form';

interface FormTextFieldProps extends Omit<TextFieldProps, 'variant'> {
    /**
     * Specifies the key in the form's data object
     */
    name: string;
    /**
     * Connects field to the form context
     */
    control: Control<any>;
    /**
     * Custom validation errors
     */
    customError?: string;
    hideError?: boolean;
    /** Strip non-digits and cap length (e.g. 10 for AU mobile). */
    digitsOnlyMaxLength?: number;
}

/**
 * For use within RHF FormProvider
 */
const FormTextField = ({
    name,
    control,
    customError,
    hideError,
    digitsOnlyMaxLength,
    onChange: userOnChange,
    onBlur: userOnBlur,
    inputProps: userInputProps,
    ...props
}: FormTextFieldProps) => {
    return (
        <Controller
            name={name}
            control={control}
            render={({
                field: { value, onChange, onBlur, ref },
                fieldState: { error },
            }) => (
                <TextField
                    {...props}
                    inputRef={ref}
                    size='small'
                    fullWidth
                    value={value ?? ''}
                    inputProps={{
                        ...userInputProps,
                        ...(digitsOnlyMaxLength != null
                            ? {
                                  inputMode: 'numeric' as const,
                                  maxLength: digitsOnlyMaxLength,
                              }
                            : {}),
                    }}
                    onChange={(e) => {
                        let next = e;
                        if (digitsOnlyMaxLength != null) {
                            const t = e.target as HTMLInputElement;
                            const cleaned = t.value
                                .replace(/\D/g, '')
                                .slice(0, digitsOnlyMaxLength);
                            next = {
                                ...e,
                                target: { ...t, value: cleaned },
                            } as React.ChangeEvent<HTMLInputElement>;
                        }
                        onChange(next);
                        userOnChange?.(next);
                    }}
                    onBlur={(e) => {
                        onBlur();
                        userOnBlur?.(e);
                    }}
                    error={!hideError && (!!error || !!customError)}
                    helperText={
                        !hideError ? error?.message || customError : undefined
                    }
                    onWheel={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.blur();
                    }}
                />
            )}
        />
    );
};

export default FormTextField;
